import { useState, useEffect, useRef } from 'react';
import { PostItNote, PostItPosition } from './PostItNote';
import { AudioEngine, ButtonColor } from '../core/AudioEngine';
import { PhysicsEngine } from '../core/PhysicsEngine';
import Matter from 'matter-js';
import './TutorialGame.css';

type ChaosStep = 'INTRUDERS' | 'PHYSICS' | 'INVISIBLE' | 'COMPLETE';

interface ChaosTutorialGameProps {
  onComplete: () => void;
  onSkip: () => void;
}

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 500;

interface Button {
  id: number;
  x: number;
  y: number;
  color: string;
  audioColor: ButtonColor;
  isIntruder?: boolean;
  physicsBody?: Matter.Body;
}

const BUTTON_COLORS = ['#FF6B6B', '#4ECDC4', '#FFD93D', '#A78BFA'];
const INTRUDER_COLOR = '#FF0000'; // Bright red for intruders
const BUTTON_AUDIO_COLORS: ButtonColor[] = ['red', 'green', 'yellow', 'blue'];

export const ChaosTutorialGame = ({ onComplete, onSkip }: ChaosTutorialGameProps) => {
  const [currentStep, setCurrentStep] = useState<ChaosStep>('INTRUDERS');
  const [buttons, setButtons] = useState<Button[]>([]);
  const [highlightedButton, setHighlightedButton] = useState<number | null>(null);
  const [blinkingButton, setBlinkingButton] = useState<number | null>(null);
  const [isPlayingSequence, setIsPlayingSequence] = useState(false);
  const [audioEngine] = useState(() => new AudioEngine());
  const [physicsEngine] = useState(() => new PhysicsEngine());
  const [postItMessage, setPostItMessage] = useState<string>('');
  const [showPostItNext, setShowPostItNext] = useState(false);
  const [postItPosition] = useState<PostItPosition>('top-center');
  const [userSequence, setUserSequence] = useState<number[]>([]);
  const [currentSequence, setCurrentSequence] = useState<number[]>([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  // Guided iteration tracking - 3 iterations per step
  const [guidedIterations, setGuidedIterations] = useState<Record<ChaosStep, number>>({
    'INTRUDERS': 0,
    'PHYSICS': 0,
    'INVISIBLE': 0,
    'COMPLETE': 0,
  });

  // Initialize audio and start first step
  useEffect(() => {
    audioEngine.init();
    startIntrudersStep();
  }, []);

  const startIntrudersStep = () => {
    setCurrentStep('INTRUDERS');
    const isGuided = guidedIterations['INTRUDERS'] < 3;
    
    if (isGuided) {
      setPostItMessage('En Chaos, aparecen botones **intrusos**. NO los toques.');
    } else {
      setPostItMessage(''); // Hide post-it after 3 iterations
    }
    
    setShowPostItNext(false);
    setShowCelebration(false);
    
    // Fixed sequence: [0, 2]
    const sequence = [0, 2];
    setCurrentSequence(sequence);
    setUserSequence([]);
    
    initButtonsWithIntruder(sequence);
    
    // Play sequence after short delay
    setTimeout(() => {
      playSequence(sequence, isGuided);
    }, 800);
  };

  const initButtonsWithIntruder = (sequence: number[]) => {
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    const newButtons: Button[] = [];
    
    // Create normal buttons
    for (let i = 0; i < 4; i++) {
      newButtons.push({
        id: i,
        x: positions[i].x,
        y: positions[i].y,
        color: BUTTON_COLORS[i],
        audioColor: BUTTON_AUDIO_COLORS[i],
        isIntruder: false,
      });
    }

    // Add one intruder at a random non-sequence position
    const nonSequenceIndices = [0, 1, 2, 3].filter(i => !sequence.includes(i));
    const intruderIndex = nonSequenceIndices[Math.floor(Math.random() * nonSequenceIndices.length)];
    
    newButtons[intruderIndex] = {
      ...newButtons[intruderIndex],
      color: INTRUDER_COLOR,
      isIntruder: true,
    };

    setButtons(newButtons);
  };

  const playSequence = async (sequence: number[], isGuided: boolean = true) => {
    setIsPlayingSequence(true);
    setUserSequence([]);
    
    for (const buttonId of sequence) {
      const button = buttons.find(b => b.id === buttonId);
      if (button) {
        setHighlightedButton(buttonId);
        audioEngine.playTone(button.audioColor, button.x, CANVAS_WIDTH, 600);
        await new Promise(resolve => setTimeout(resolve, 600));
        setHighlightedButton(null);
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    setIsPlayingSequence(false);
    
    // Start blinking first correct button only if guided
    if (isGuided) {
      setBlinkingButton(sequence[0]);
    }
  };

  const handleButtonClick = (buttonId: number) => {
    if (isPlayingSequence || showCelebration) return;

    const button = buttons.find(b => b.id === buttonId);
    if (!button) return;

    // Step-specific logic
    if (currentStep === 'INTRUDERS') {
      // Check if clicked an intruder
      if (button.isIntruder) {
        audioEngine.playTone('red', 300, CANVAS_WIDTH, 300); // Error sound
        const isGuided = guidedIterations['INTRUDERS'] < 3;
        if (isGuided) {
          setPostItMessage('¡Ese es un intruso! Evítalo.');
        }
        
        // Replay sequence after short delay
        setTimeout(() => {
          if (isGuided) {
            setPostItMessage('En Chaos, aparecen botones **intrusos**. NO los toques.');
          }
          playSequence(currentSequence, isGuided);
        }, 1500);
        return;
      }
    }

    // Play button sound
    audioEngine.playTone(button.audioColor, button.x, CANVAS_WIDTH, 300);
    setHighlightedButton(buttonId);
    setTimeout(() => setHighlightedButton(null), 300);

    const newUserSequence = [...userSequence, buttonId];
    setUserSequence(newUserSequence);

    // Check if correct so far
    const expectedIndex = newUserSequence.length - 1;
    if (currentSequence[expectedIndex] !== buttonId) {
      // Wrong button
      audioEngine.playTone('red', 300, CANVAS_WIDTH, 300);
      const isGuided = guidedIterations[currentStep] < 3;
      setTimeout(() => {
        if (currentStep === 'PHYSICS') {
          playSequenceWithPhysics(currentSequence, isGuided);
        } else if (currentStep === 'INVISIBLE') {
          playSequenceInvisible(currentSequence);
        } else {
          playSequence(currentSequence, isGuided);
        }
      }, 800);
      return;
    }

    // Update blink to next button (only if still in guided mode)
    const isGuided = guidedIterations[currentStep] < 3;
    if (newUserSequence.length < currentSequence.length) {
      if (isGuided) {
        setBlinkingButton(currentSequence[newUserSequence.length]);
      }
    } else {
      // Sequence complete!
      setBlinkingButton(null);
      
      // Increment guided iteration counter
      const newCount = guidedIterations[currentStep] + 1;
      setGuidedIterations(prev => ({ ...prev, [currentStep]: newCount }));
      
      // If still guided, show celebration and replay
      if (newCount < 3) {
        setShowCelebration(true);
        
        if (currentStep === 'INTRUDERS') {
          setPostItMessage(`¡Perfecto! Evitaste el intruso 🎉 (${newCount}/3)`);
        } else if (currentStep === 'PHYSICS') {
          setPostItMessage(`¡Bien! Lograste seguir el movimiento 🎯 (${newCount}/3)`);
        } else if (currentStep === 'INVISIBLE') {
          setPostItMessage(`¡Increíble! Dominaste el modo invisible 👻 (${newCount}/3)`);
        }
        
        // Auto-restart after short delay
        setTimeout(() => {
          if (currentStep === 'INTRUDERS') {
            startIntrudersStep();
          } else if (currentStep === 'PHYSICS') {
            startPhysicsStep();
          } else if (currentStep === 'INVISIBLE') {
            startInvisibleStep();
          }
        }, 1500);
      } else {
        // After 3 iterations, allow advancing to next step
        setShowCelebration(true);
        
        if (currentStep === 'INTRUDERS') {
          setPostItMessage('¡Perfecto! Has dominado los intrusos 🎉');
        } else if (currentStep === 'PHYSICS') {
          setPostItMessage('¡Bien! Dominaste el movimiento 🎯');
        } else if (currentStep === 'INVISIBLE') {
          setPostItMessage('¡Increíble! Dominaste el modo invisible 👻');
        }
        
        setShowPostItNext(true);
      }
    }
  };

  const startCompleteStep = () => {
    setCurrentStep('COMPLETE');
    setPostItMessage('¡Listo para Chaos!');
    setShowPostItNext(false);
    setShowCelebration(true);
    setButtons([]); // Clear board
    
    // Mark Chaos tutorial as completed
    // Note: This will be integrated with storage.ts in final version
    // localStorage.setItem('simon-revolutions:tutorial-chaos-completed', 'true');
  };

  const startInvisibleStep = () => {
    setCurrentStep('INVISIBLE');
    const isGuided = guidedIterations['INVISIBLE'] < 3;
    
    if (isGuided) {
      setPostItMessage('Modo invisible: cierra los ojos y confía en el audio posicional.');
    } else {
      setPostItMessage('');
    }
    
    setShowPostItNext(false);
    setShowCelebration(false);
    
    // Fixed sequence: [2, 1]
    const sequence = [2, 1];
    setCurrentSequence(sequence);
    setUserSequence([]);
    
    initButtonsInvisible();
    
    // Play sequence after short delay
    setTimeout(() => {
      playSequenceInvisible(sequence);
    }, 1000);
  };

  const initButtonsInvisible = () => {
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    const newButtons: Button[] = [];
    
    // Create invisible buttons (opacity 0 in rendering)
    for (let i = 0; i < 4; i++) {
      newButtons.push({
        id: i,
        x: positions[i].x,
        y: positions[i].y,
        color: BUTTON_COLORS[i],
        audioColor: BUTTON_AUDIO_COLORS[i],
      });
    }

    setButtons(newButtons);
  };

  const playSequenceInvisible = async (sequence: number[]) => {
    setIsPlayingSequence(true);
    setUserSequence([]);
    
    for (const buttonId of sequence) {
      const button = buttons.find(b => b.id === buttonId);
      if (button) {
        // Audio only, no visual highlight
        audioEngine.playTone(button.audioColor, button.x, CANVAS_WIDTH, 800);
        await new Promise(resolve => setTimeout(resolve, 800));
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    setIsPlayingSequence(false);
    // No blink in invisible mode
  };

  const startPhysicsStep = () => {
    setCurrentStep('PHYSICS');
    const isGuided = guidedIterations['PHYSICS'] < 3;
    
    if (isGuided) {
      setPostItMessage('Los botones pueden flotar y rebotar. Mantén el ritmo visual.');
    } else {
      setPostItMessage('');
    }
    
    setShowPostItNext(false);
    setShowCelebration(false);
    
    // Fixed sequence: [1, 3, 0]
    const sequence = [1, 3, 0];
    setCurrentSequence(sequence);
    setUserSequence([]);
    
    initButtonsWithPhysics();
    
    // Start physics animation
    physicsEngine.start();
    startPhysicsAnimation();
    
    // Play sequence after short delay
    setTimeout(() => {
      playSequenceWithPhysics(sequence, isGuided);
    }, 1000);
  };

  const initButtonsWithPhysics = () => {
    // Initialize physics world
    physicsEngine.init(CANVAS_WIDTH, CANVAS_HEIGHT, 50);
    
    const positions = [
      { x: 200, y: 150 },
      { x: 400, y: 150 },
      { x: 200, y: 350 },
      { x: 400, y: 350 },
    ];

    const newButtons: Button[] = [];
    
    // Create buttons with physics bodies (reduced velocity ~50%)
    for (let i = 0; i < 4; i++) {
      const vx = (Math.random() - 0.5) * 1.0; // Reduced from 2.0
      const vy = (Math.random() - 0.5) * 1.0; // Reduced from 2.0
      
      const body = physicsEngine.createButton(
        BUTTON_AUDIO_COLORS[i],
        positions[i].x,
        positions[i].y,
        vx,
        vy
      );
      
      newButtons.push({
        id: i,
        x: positions[i].x,
        y: positions[i].y,
        color: BUTTON_COLORS[i],
        audioColor: BUTTON_AUDIO_COLORS[i],
        physicsBody: body,
      });
    }

    setButtons(newButtons);
  };

  const startPhysicsAnimation = () => {
    const animate = () => {
      // Update button positions from physics bodies
      setButtons(prevButtons => 
        prevButtons.map(button => {
          if (button.physicsBody) {
            return {
              ...button,
              x: button.physicsBody.position.x,
              y: button.physicsBody.position.y,
            };
          }
          return button;
        })
      );
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  };

  const playSequenceWithPhysics = async (sequence: number[], isGuided: boolean = true) => {
    setIsPlayingSequence(true);
    setUserSequence([]);
    
    for (const buttonId of sequence) {
      const button = buttons.find(b => b.id === buttonId);
      if (button) {
        setHighlightedButton(buttonId);
        audioEngine.playTone(button.audioColor, button.x, CANVAS_WIDTH, 600);
        await new Promise(resolve => setTimeout(resolve, 600));
        setHighlightedButton(null);
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    setIsPlayingSequence(false);
    
    // Start blinking first correct button only if guided
    if (isGuided) {
      setBlinkingButton(sequence[0]);
    }
  };

  const handleNext = () => {
    if (currentStep === 'INTRUDERS') {
      startPhysicsStep();
    } else if (currentStep === 'PHYSICS') {
      // Stop physics animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      physicsEngine.stop();
      
      startInvisibleStep();
    } else if (currentStep === 'INVISIBLE') {
      startCompleteStep();
    }
  };

  const handleSkip = () => {
    // Cleanup physics if active
    if (currentStep === 'PHYSICS' && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      physicsEngine.stop();
    }
    onSkip();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      physicsEngine.stop();
    };
  }, []);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    buttons.forEach((button) => {
      const isHighlighted = highlightedButton === button.id;
      const isBlinking = blinkingButton === button.id;

      // Skip rendering in INVISIBLE mode (buttons still clickable)
      if (currentStep === 'INVISIBLE') {
        return;
      }

      ctx.beginPath();
      ctx.arc(button.x, button.y, 50, 0, Math.PI * 2);
      
      if (button.isIntruder) {
        // Intruder: red with red blink
        ctx.fillStyle = button.color;
        ctx.strokeStyle = isBlinking ? '#FF0000' : '#000000';
        ctx.lineWidth = isBlinking ? 6 : 2;
      } else {
        ctx.fillStyle = isHighlighted ? '#FFFFFF' : button.color;
        ctx.strokeStyle = isBlinking ? '#FFD700' : '#000000'; // Golden blink for correct buttons
        ctx.lineWidth = isBlinking ? 6 : 2;
      }
      
      ctx.fill();
      ctx.stroke();
    });
  }, [buttons, highlightedButton, blinkingButton, currentStep]);

  // Blink animation
  useEffect(() => {
    if (blinkingButton === null) return;

    const interval = setInterval(() => {
      setBlinkingButton((prev) => (prev !== null ? null : currentSequence[userSequence.length]));
    }, 500);

    return () => clearInterval(interval);
  }, [blinkingButton, userSequence.length, currentSequence]);

  return (
    <div className="tutorial-game">
      <div className="tutorial-header">
        <h1>Tutorial Chaos</h1>
        {currentStep !== 'COMPLETE' && (
          <button className="skip-button" onClick={handleSkip}>
            Saltar Tutorial
          </button>
        )}
      </div>

      {currentStep === 'COMPLETE' ? (
        <div className="tutorial-complete">
          <PostItNote
            message={postItMessage}
            position="top-center"
            showNextButton={false}
            onNext={() => {}}
          />
          <div className="tutorial-summary">
            <h2>Aprendiste:</h2>
            <ul>
              <li>✅ Evita intrusos</li>
              <li>✅ Sigue botones en movimiento</li>
              <li>✅ Confía en el audio cuando sean invisibles</li>
            </ul>
            <div className="tutorial-actions">
              <button className="primary-button" onClick={onComplete}>
                ¡Jugar Chaos!
              </button>
              <button className="secondary-button" onClick={onSkip}>
                Cambiar Modo
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {postItMessage && (
            <PostItNote
              message={postItMessage}
              position={postItPosition}
              showNextButton={showPostItNext}
              onNext={handleNext}
            />
          )}

          {currentStep === 'INVISIBLE' && !isPlayingSequence && (
            <div className="replay-controls">
              <button 
                className="replay-button" 
                onClick={() => playSequenceInvisible(currentSequence)}
              >
                🔁 Reproducir Secuencia
              </button>
            </div>
          )}

          <canvas
            ref={canvasRef}
            width={600}
            height={500}
            className="game-canvas"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;

              buttons.forEach((button) => {
                const distance = Math.sqrt((x - button.x) ** 2 + (y - button.y) ** 2);
                if (distance <= 50) {
                  handleButtonClick(button.id);
                }
              });
            }}
            style={{ cursor: 'pointer' }}
          />

          {showCelebration && (
            <div className="celebration">
              <span className="celebration-emoji">🎉</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
